import { useCallback, useState } from 'react'
import { Info } from 'lucide-react'
import { useHelpTopics } from '../WebModulesProvider.js'
import { HelpPanel } from './HelpPanel.js'
import { useHelpSeat } from './HelpSeatContext.js'
import { markTopicSeen, readSeenTopics } from './helpSeen.js'
import type { HelpTopic } from './types.js'

/**
 * The discreet "i" beside a screen or card title that opens its help
 * (docs/plans/in-app-help-plan.md). It renders nothing where the topic is not
 * written yet or is not for the reader's seat, so an icon can be placed before
 * its words exist. Until this browser has opened the topic it wears a small
 * dot, for the new user; nothing ever opens by itself.
 */
export function HelpIcon({ topic: topicId, className = '' }: { topic: string; className?: string }) {
  const seat = useHelpSeat()
  const topics = useHelpTopics()
  const topicFor = useCallback((id: string): HelpTopic | null => {
    const found = topics.find((entry) => entry.id === id)
    return found && seat && found.seats.includes(seat) ? found : null
  }, [topics, seat])
  const [open, setOpen] = useState<HelpTopic | null>(null)
  const [seen, setSeen] = useState(() => readSeenTopics().has(topicId))
  const close = useCallback(() => setOpen(null), [])

  const topic = topicFor(topicId)
  if (!topic || !seat) {
    return null
  }
  function show(next: HelpTopic): void {
    markTopicSeen(next.id)
    if (next.id === topicId) {
      setSeen(true)
    }
    setOpen(next)
  }

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          show(topic)
        }}
        aria-label={`About ${topic.title}`}
        title={`About ${topic.title}`}
        className={`relative inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full align-middle text-ink-mid/70 normal-case tracking-normal hover:bg-marine/10 hover:text-marine focus:outline-none focus-visible:ring-2 focus-visible:ring-marine/40 ${className}`}
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
        {!seen && <span aria-hidden="true" className="absolute right-0 top-0 h-1.5 w-1.5 rounded-full bg-marine" />}
      </button>
      {open && <HelpPanel topic={open} seat={seat} topicById={topicFor} onOpenTopic={show} onClose={close} />}
    </>
  )
}
